import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Crown, Sparkles, LogOut, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PACKS } from '@/data/templates';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (e) {
    return iso;
  }
}

export default function Account() {
  const { user, loading, logout } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;

  const planLabel = user.plan === 'lifetime' ? 'Lifetime' : user.is_pro ? 'Pro' : 'Free';
  const ownedPacks = (user.owned_packs || []).map(slug => PACKS.find(p => p.slug === slug)).filter(Boolean);

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">Account</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Hey, {(user.name || user.email).split(' ')[0]}</h1>
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl p-6 mb-6 flex items-center gap-4" data-testid="account-card">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-stone-200" />
        )}
        <div className="flex-1">
          <div className="font-display font-bold text-lg">{user.name || '—'}</div>
          <div className="text-sm text-stone-500">{user.email}</div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Current plan</div>
            <div className="flex items-center gap-2">
              <span className="font-display font-black text-3xl tracking-tight">{planLabel}</span>
              {user.is_pro && <Crown size={20} className="text-orange-500" />}
            </div>
            {user.is_pro && user.plan !== 'lifetime' && (
              <div className="text-sm text-stone-500 mt-2">Renews on <strong className="text-stone-900">{formatDate(user.subscription_until)}</strong></div>
            )}
            {user.plan === 'lifetime' && (
              <div className="text-sm text-stone-500 mt-2">Pro access forever. No further charges.</div>
            )}
            {!user.is_pro && (
              <div className="text-sm text-stone-500 mt-2">You're on Free — downloads include a watermark and AI is disabled.</div>
            )}
          </div>
          {!user.is_pro && (
            <Link
              to="/pricing"
              data-testid="account-upgrade-btn"
              className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-full text-sm font-medium btn-clay shrink-0"
            >
              <Sparkles size={14} /> Upgrade
            </Link>
          )}
        </div>
      </div>

      {!user.is_pro && ownedPacks.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-3xl p-6 mb-6" data-testid="account-packs">
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">Your packs</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {ownedPacks.map(pack => (
              <div key={pack.slug} className="flex items-center gap-3 p-4 rounded-2xl border border-emerald-200 bg-emerald-50/50" data-testid={`account-pack-${pack.slug}`}>
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                <div>
                  <div className="font-medium text-sm">{pack.name}</div>
                  <div className="text-xs text-stone-500">Watermark-free downloads unlocked</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-3xl p-6 mb-6">
        <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">Quick actions</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to="/" className="flex items-center justify-between p-4 rounded-2xl border border-stone-200 hover:border-orange-400 transition-colors">
            <span className="font-medium text-sm">Browse templates</span>
            <ArrowRight size={14} />
          </Link>
          <Link to="/tools" className="flex items-center justify-between p-4 rounded-2xl border border-stone-200 hover:border-orange-400 transition-colors">
            <span className="font-medium text-sm">Open PDF tools</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <button
        onClick={logout}
        data-testid="account-logout-btn"
        className="inline-flex items-center gap-2 text-stone-500 hover:text-red-600 text-sm"
      >
        <LogOut size={14} /> Sign out
      </button>
    </div>
  );
}
